import { useMemo, useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getQuotesSummary,
  saveQuotesSelection,
  type QuotesSummaryResponse,
  type QuotesSummaryPosition,
} from '../../../../shared/api/quotesSelection.api'

export type QuoteFilter = 'all' | 'ready' | 'waiting' | 'no-quotes'

/**
 * Хук страницы «Выбор квот»: загрузка сводки, локальный map выбора (positionId → quoteId),
 * фильтрация по статусу, фиксация несохранённых изменений и батч-сохранение.
 * Локальный выбор инициализируется из selectedQuoteId с бэка и перезаписывается
 * менеджером через setSelection(). isDirty сравнивает текущий map с исходным.
 * Снять выбор нельзя — только сменить на другую квоту (решение Сергея
 * 14.09.2026): сохранение отправляет лишь выбранные квоты, поэтому «снятие»
 * не доходило до сервера и после обновления страницы выбор возвращался.
 */
export function useQuotesSelection(requestId: string | undefined) {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ['quotes-summary', requestId],
    queryFn: () => getQuotesSummary(requestId!),
    enabled: !!requestId,
  })

  const [selection, setSelectionState] = useState<Record<string, string | null>>({})
  const [filter, setFilter] = useState<QuoteFilter>('all')

  // Инициализация локального выбора из ответа бэка.
  useEffect(() => {
    if (!query.data) return
    const initial: Record<string, string | null> = {}
    for (const p of query.data.positions) {
      initial[p.id] = p.selectedQuoteId
    }
    setSelectionState(initial)
  }, [query.data])

  const initialSelection = useMemo<Record<string, string | null>>(() => {
    if (!query.data) return {}
    const map: Record<string, string | null> = {}
    for (const p of query.data.positions) map[p.id] = p.selectedQuoteId
    return map
  }, [query.data])

  const isDirty = useMemo(() => {
    const ids = new Set([...Object.keys(selection), ...Object.keys(initialSelection)])
    for (const id of ids) {
      if ((selection[id] ?? null) !== (initialSelection[id] ?? null)) return true
    }
    return false
  }, [selection, initialSelection])

  const setSelection = (positionId: string, quoteId: string) => {
    setSelectionState((prev) => ({ ...prev, [positionId]: quoteId }))
  }

  const filteredPositions = useMemo<QuotesSummaryPosition[]>(() => {
    if (!query.data) return []
    if (filter === 'all') return query.data.positions
    return query.data.positions.filter((p) => {
      const sel = selection[p.id] ?? null
      if (filter === 'ready') return sel !== null
      if (filter === 'waiting') return sel === null && p.quotes.length > 0
      if (filter === 'no-quotes') return p.quotes.length === 0
      return true
    })
  }, [query.data, filter, selection])

  const saveMutation = useMutation({
    mutationFn: (payload: QuotesSummaryResponse) => {
      const selections = payload.positions
        .map((p) => ({ positionId: p.id, quoteId: selection[p.id] ?? null }))
        .filter((s) => s.quoteId !== null && s.quoteId !== initialSelection[s.positionId])
      if (selections.length === 0) return Promise.resolve({ updated: 0 })
      return saveQuotesSelection(requestId!, { selections })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['quotes-summary', requestId] })
      void queryClient.invalidateQueries({ queryKey: ['requests', requestId] })
    },
  })

  // Пересчитывает ВСЕ позиции у которых есть квота isMin — в том числе те,
  // где уже выбрана не-минимальная. Ручной выбор перетирается: менеджер
  // нажал «Авто: минимальные цены» → получил минимальные.
  const autoSelectMin = () => {
    if (!query.data) return 0
    let count = 0
    const next = { ...selection }
    for (const p of query.data.positions) {
      const minQuote = p.quotes.find((q) => q.isMin)
      if (!minQuote) continue
      if ((selection[p.id] ?? null) === minQuote.id) continue
      next[p.id] = minQuote.id
      count += 1
    }
    if (count > 0) setSelectionState(next)
    return count
  }

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    selection,
    setSelection,
    isDirty,
    filter,
    setFilter,
    filteredPositions,
    save: () => query.data && saveMutation.mutateAsync(query.data),
    isSaving: saveMutation.isPending,
    saveError: saveMutation.error,
    autoSelectMin,
  }
}
